// GradeJourneyAdmin.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

import GradeExtractionProcessesPage from "./GradeExtractionProcessesPage";
import IncomingSettingsPage from "./IncomingSettingsPage";
import NotificationSettingsPage from "./NotificationSettingsPage";
import AdminToolsPage from "./AdminToolsPage";

/**
 * GradeJourneyAdmin (entry point / controller)
 *
 * Props:
 *  - platformUrl (string): Learn base URL
 *  - backEndUrl? (string): backend base (no /Prod/api)
 *
 * URL params:
 *  - uuid (user uuid)  - for SC_ADMIN role check
 *  - token (Bearer token) - for Learn/Backend calls
 */

function useQuery() {
    const location = useLocation();
    return new URLSearchParams(location.search);
}

const defaultConfig = {
    gradeExportAvailable: true,
    gradesJourneyFeatures: true,
    gradebookExtract: true
};

const defaultProcesses = [
    {
        id: "grade-export-v2",
        name: "Grade Export v2",
        description:
            "The Blackboard Learn Grade Export feature allows an ins...",
        type: "APPROVAL_WORKFLOW",
        enabled: false,
        lastEvent: "2024-06-10T09:10:04"
    },
    {
        id: "tg-session-2",
        name: "TG Session 2",
        description: "",
        type: "GRADEBOOK_EXTRACT",
        enabled: false,
        lastEvent: "2024-06-10T09:10:04"
    },
    {
        id: "cont-ed-fall-2025",
        name: "Continuing Education - Fall 2025",
        description: "",
        type: "GRADEBOOK_EXTRACT",
        enabled: false,
        lastEvent: "2024-06-10T09:10:04"
    },
    {
        id: "professional-development",
        name: "Professional Development",
        description: "Automated extract for Professional Development courses",
        type: "GRADEBOOK_EXTRACT",
        enabled: false,
        lastEvent: "2025-11-11T17:35:03"
    },
    {
        id: "invalid-grade-notification",
        name: "InvalidGradeNotification",
        description: "Used by Tim Grady to Test Invalid Grade Notifications",
        type: "GRADEBOOK_EXTRACT",
        enabled: false,
        lastEvent: "2025-11-11T17:35:03"
    },
    {
        id: "tg-session-1",
        name: "TG-Session1",
        description: "",
        type: "GRADEBOOK_EXTRACT",
        enabled: false,
        lastEvent: "2024-06-10T08:45:02"
    },
    {
        id: "sbt-extract",
        name: "SBTestExtract",
        description: "Qatar Test",
        type: "GRADEBOOK_EXTRACT",
        enabled: false,
        lastEvent: "2025-02-07T13:57:08"
    },
    {
        id: "tg-full-session",
        name: "TG Full Session",
        description: "",
        type: "GRADEBOOK_EXTRACT",
        enabled: false,
        lastEvent: "2024-06-10T09:15:02"
    }
];

const tabs = [
    { key: "processes", label: "Grade Extraction Processes" },
    { key: "incoming", label: "Incoming Settings" },
    { key: "notifications", label: "Notification Settings" },
    { key: "admin-tools", label: "Admin Tools" }
];

const GradeJourneyAdmin = ({ platformUrl, backEndUrl }) => {
    const query = useQuery();
    const urlUuid = query.get("uuid") || "";
    const token = query.get("token") || "";

    const [config, setConfig] = useState(defaultConfig);
    const [processes, setProcesses] = useState(defaultProcesses);

    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(!!backEndUrl);
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [activeTab, setActiveTab] = useState("processes");

    const authHeaders = useMemo(
        () => (token ? { Authorization: `Bearer ${token}` } : {}),
        [token]
    );

    /* ---- Role check ---- */
    useEffect(() => {
        let cancelled = false;

        const fetchUser = async () => {
            if (!platformUrl || !urlUuid || !token) {
                if (!cancelled) setIsAdmin(false);
                return;
            }
            try {
                const res = await axios.get(
                    `${platformUrl}/learn/api/public/v1/users/uuid:${urlUuid}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const u = res?.data || {};
                const roles = u?.institutionRoleIds || [];
                const admin = Array.isArray(roles) && roles.includes("SC_ADMIN");
                if (!cancelled) setIsAdmin(!!admin);
            } catch (err) {
                console.error("Failed to fetch user info for role check:", err);
                if (!cancelled) setIsAdmin(false);
            }
        };

        fetchUser();
        return () => {
            cancelled = true;
        };
    }, [platformUrl, urlUuid, token]);

    /* ---- Initial load from backend ---- */
    useEffect(() => {
        let cancelled = false;
        if (!backEndUrl) return;

        const loadAll = async () => {
            try {
                setLoading(true);
                setError("");
                setInfo("");

                // Config
                try {
                    const cfgRes = await axios.get(
                        `${backEndUrl}/gradesjourney/config`,
                        { headers: authHeaders }
                    );
                    if (!cancelled && cfgRes?.data) {
                        setConfig((prev) => ({ ...prev, ...cfgRes.data }));
                    }
                } catch (cfgErr) {
                    console.warn("Failed to load Grades Journey config; using defaults.", cfgErr);
                }

                // Processes
                try {
                    const procRes = await axios.get(
                        `${backEndUrl}/gradesjourney/processes`,
                        { headers: authHeaders }
                    );
                    let data = [];
                    if (Array.isArray(procRes.data?.items)) data = procRes.data.items;
                    else if (Array.isArray(procRes.data)) data = procRes.data;

                    if (!cancelled) {
                        if (data.length) {
                            setProcesses(data);
                        } else {
                            setProcesses(defaultProcesses);
                            setInfo(
                                "No grade extraction processes found in the backend. Showing sample set."
                            );
                        }
                    }
                } catch (procErr) {
                    console.error("Failed to load processes; using defaults.", procErr);
                    if (!cancelled) {
                        setProcesses(defaultProcesses);
                        setError(
                            procErr?.response?.data?.message ||
                            "Failed to load grade extraction processes. Showing sample data."
                        );
                    }
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadAll();
        return () => {
            cancelled = true;
        };
    }, [backEndUrl, authHeaders]);

    const refetchProcesses = async () => {
        if (!backEndUrl) return;
        try {
            setLoading(true);
            const res = await axios.get(`${backEndUrl}/gradesjourney/processes`, {
                headers: authHeaders
            });
            const data = Array.isArray(res.data?.items) ? res.data.items : [];
            if (data.length) setProcesses(data);
        } catch (err) {
            console.error("Refetch processes failed:", err);
        } finally {
            setLoading(false);
        }
    };

    /* ---- Config handlers ---- */

    const saveConfigToBackend = async (nextConfig) => {
        if (!backEndUrl) return;
        try {
            await axios.put(`${backEndUrl}/gradesjourney/config`, nextConfig, {
                headers: { ...authHeaders, "Content-Type": "application/json" }
            });
            setInfo("Grades Journey configuration saved.");
        } catch (err) {
            console.error("Saving config failed:", err);
            setError(err?.response?.data?.message || "Failed to save configuration.");
        }
    };

    const toggleConfig = (field) => {
        if (!isAdmin) return;
        setConfig((prev) => {
            const next = { ...prev, [field]: !prev[field] };
            saveConfigToBackend(next);
            return next;
        });
    };

    /* ---- Process CRUD handlers ---- */

    const ensureProcessId = (name, fallbackIndex) => {
        const base =
            String(name || `process-${fallbackIndex}-${Date.now()}`)
                .toLowerCase()
                .replace(/\s+/g, "-")
                .replace(/[^a-z0-9\-]/g, "") || `process-${fallbackIndex}-${Date.now()}`;
        let candidate = base;
        let suffix = 1;
        const existingIds = new Set(processes.map((p) => p.id));
        while (existingIds.has(candidate)) {
            candidate = `${base}-${suffix++}`;
        }
        return candidate;
    };

    const saveProcessToBackend = async (proc) => {
        if (!backEndUrl) return;
        try {
            await axios.put(`${backEndUrl}/gradesjourney/processes`, proc, {
                headers: { ...authHeaders, "Content-Type": "application/json" }
            });
        } catch (err) {
            if (err?.response?.status === 409) {
                await axios.patch(`${backEndUrl}/gradesjourney/processes`, proc, {
                    headers: { ...authHeaders, "Content-Type": "application/json" }
                });
            } else {
                throw err;
            }
        }
    };

    const handleCreateProcess = async (partial) => {
        const name = (partial.name || "").trim();
        if (!name) {
            setError("Name is required.");
            throw new Error("Name required");
        }

        const id =
            (partial.id || "").trim() ||
            ensureProcessId(name, processes.length + 1);

        if (processes.some((p) => p.id === id)) {
            const msg = `A process with id "${id}" already exists.`;
            setError(msg);
            throw new Error(msg);
        }

        const payload = {
            id,
            name,
            description: (partial.description || "").trim(),
            type: partial.type || "GRADEBOOK_EXTRACT",
            enabled: !!partial.enabled,
            lastEvent: partial.lastEvent || ""
        };

        setProcesses((prev) => [...prev, payload]);

        try {
            await saveProcessToBackend(payload);
            setInfo(`Added process "${payload.name}".`);
            await refetchProcesses();
        } catch (err) {
            console.error("Add process failed:", err);
            setError(err?.response?.data?.message || "Failed to add process.");
        }
    };

    const handleUpdateProcess = async (payload) => {
        if (!payload || !payload.id) return;

        setProcesses((prev) =>
            prev.map((p) => (p.id === payload.id ? { ...p, ...payload } : p))
        );

        try {
            await saveProcessToBackend(payload);
            setInfo(`Saved process "${payload.name}".`);
            await refetchProcesses();
        } catch (err) {
            console.error("Save process failed:", err);
            setError(err?.response?.data?.message || "Failed to save process.");
        }
    };

    const handleDeleteProcess = async (proc) => {
        setProcesses((prev) => prev.filter((p) => p.id !== proc.id));

        if (backEndUrl) {
            try {
                await axios.delete(`${backEndUrl}/gradesjourney/processes`, {
                    headers: { ...authHeaders, "Content-Type": "application/json" },
                    data: { id: proc.id }
                });
                setInfo(`Deleted process "${proc.name}".`);
            } catch (err) {
                console.error("Delete process failed:", err);
                setError(
                    err?.response?.data?.message ||
                    "Failed to delete process (local removal kept)."
                );
            }
        }
    };

    const handleRunProcess = async (proc) => {
        if (!backEndUrl) {
            window.alert("This would trigger the extract in the real system.");
            return;
        }
        try {
            setInfo("");
            setError("");
            await axios.post(
                `${backEndUrl}/gradesjourney/processes/run`,
                { id: proc.id },
                { headers: { ...authHeaders, "Content-Type": "application/json" } }
            );
            setInfo(`Run requested for "${proc.name}".`);
        } catch (err) {
            console.error("Run process failed:", err);
            setError(err?.response?.data?.message || "Failed to run process.");
        }
    };

    const handleToggleProcessEnabled = async (proc) => {
        const updated = { ...proc, enabled: !proc.enabled };
        await handleUpdateProcess(updated);
    };

    /* ---- Render ---- */

    return (
        <div className="bb-container">
            {/* Top configuration bar */}
            <GradeExportAvailabilitySection
                isAdmin={isAdmin}
                gradeExportAvailable={config.gradeExportAvailable}
                onToggle={() => toggleConfig("gradeExportAvailable")}
            />

            <hr style={{ border: 0, borderTop: "1px solid #ddd", margin: "8px 0 16px" }} />

            <JourneyFeaturesSection
                isAdmin={isAdmin}
                gradesJourneyFeatures={config.gradesJourneyFeatures}
                gradebookExtract={config.gradebookExtract}
                onToggleFeatures={() => toggleConfig("gradesJourneyFeatures")}
                onToggleGradebookExtract={() => toggleConfig("gradebookExtract")}
            />

            <hr style={{ border: 0, borderTop: "1px solid #ddd", margin: "16px 0 8px" }} />

            <div style={{ marginBottom: 8 }}>
                <h4 style={{ margin: 0, fontWeight: 600 }}>SETTINGS</h4>
            </div>

            <SettingsTabs
                tabs={tabs}
                activeTab={activeTab}
                onChange={setActiveTab}
            />

            {/* Notices */}
            {!!info && (
                <div className="bb-info" style={{ margin: "4px 0 8px", color: "#125" }}>
                    {info}
                </div>
            )}
            {!!error && (
                <div className="bb-error" style={{ margin: "4px 0 8px", color: "#a00" }}>
                    {error}
                </div>
            )}
            {loading && (
                <div className="bb-note" style={{ marginBottom: 8 }}>
                    Loading Grades Journey settings…
                </div>
            )}

            {/* Subpages */}
            {activeTab === "processes" && (
                <GradeExtractionProcessesPage
                    isAdmin={isAdmin}
                    processes={processes}
                    onCreate={handleCreateProcess}
                    onUpdate={handleUpdateProcess}
                    onDelete={handleDeleteProcess}
                    onRun={handleRunProcess}
                    onToggleEnabled={handleToggleProcessEnabled}
                />
            )}

            {activeTab === "incoming" && <IncomingSettingsPage />}

            {activeTab === "notifications" && <NotificationSettingsPage />}

            {activeTab === "admin-tools" && <AdminToolsPage />}
        </div>
    );
};

/* ---------- shared small view components (keep in this file) ---------- */

const GradeExportAvailabilitySection = ({
                                            isAdmin,
                                            gradeExportAvailable,
                                            onToggle
                                        }) => (
    <div
        style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 16,
            marginTop: 4
        }}
    >
        <h3 style={{ margin: 0, fontWeight: 600 }}>GRADE EXPORT AVAILABILITY</h3>
        <label
            style={{
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                opacity: isAdmin ? 1 : 0.6
            }}
        >
            <span>On / Off</span>
            <input
                type="checkbox"
                checked={gradeExportAvailable}
                disabled={!isAdmin}
                onChange={onToggle}
            />
        </label>
    </div>
);

const JourneyFeaturesSection = ({
                                    isAdmin,
                                    gradesJourneyFeatures,
                                    gradebookExtract,
                                    onToggleFeatures,
                                    onToggleGradebookExtract
                                }) => (
    <div>
        <h4 style={{ margin: "0 0 8px", fontWeight: 600 }}>
            GRADES JOURNEY FEATURES
        </h4>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "220px auto 80px",
                    alignItems: "center",
                    columnGap: 16
                }}
            >
                <div style={{ fontWeight: 600 }}>Grades Journey Features</div>
                <div style={{ fontSize: 12, opacity: 0.8 }}>
                    Grades Journey is a Blackboard Learn Extension that enhances the free
                    Grade Export feature by automating grading and content provisioning
                    workflows. It also allows multiple extractions, customizations on grade
                    schemas management, and adding submitter roles before exporting grades
                    to the SIS.
                </div>
                <div style={{ textAlign: "right" }}>
                    <input
                        type="checkbox"
                        checked={gradesJourneyFeatures}
                        disabled={!isAdmin}
                        onChange={onToggleFeatures}
                    />
                </div>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "220px auto 80px",
                    alignItems: "center",
                    columnGap: 16
                }}
            >
                <div style={{ fontWeight: 600 }}>Gradebook Extract</div>
                <div style={{ fontSize: 12, opacity: 0.8 }}>
                    Enable this option if you want to configure one or multiple grade
                    extractions to not require the standard Grade Export approval workflow.
                </div>
                <div style={{ textAlign: "right" }}>
                    <input
                        type="checkbox"
                        checked={gradebookExtract}
                        disabled={!isAdmin}
                        onChange={onToggleGradebookExtract}
                    />
                </div>
            </div>
        </div>
    </div>
);

const SettingsTabs = ({ tabs, activeTab, onChange }) => (
    <div
        style={{
            display: "flex",
            flexWrap: "wrap",
            borderBottom: "1px solid #ddd",
            marginBottom: 12,
            gap: 4
        }}
    >
        {tabs.map((t) => {
            const active = t.key === activeTab;
            return (
                <button
                    key={t.key}
                    type="button"
                    onClick={() => onChange(t.key)}
                    style={{
                        border: "none",
                        borderBottom: active
                            ? "2px solid #0073aa"
                            : "2px solid transparent",
                        background: "transparent",
                        padding: "6px 10px",
                        fontSize: 13,
                        cursor: "pointer",
                        fontWeight: active ? 600 : 400,
                        color: active ? "#0073aa" : "#333"
                    }}
                >
                    {t.label}
                </button>
            );
        })}
    </div>
);

export default GradeJourneyAdmin;
